// src/pages/buyer/ReturnsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { returnsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { RotateCcw, Package, ChevronRight, Plus } from 'lucide-react'

const STATUS_COLORS = {
  requested:        'bg-amber-100 text-amber-700',
  approved:         'bg-blue-100 text-blue-700',
  pickup_scheduled: 'bg-indigo-100 text-indigo-700',
  picked_up:        'bg-purple-100 text-purple-700',
  inspected:        'bg-cyan-100 text-cyan-700',
  refund_initiated: 'bg-green-100 text-green-700',
  completed:        'bg-green-100 text-green-700',
  rejected:         'bg-red-100 text-red-700',
}

const REASONS = [
  { value: 'defective', label: 'Defective Product' },
  { value: 'wrong_item', label: 'Wrong Item Received' },
  { value: 'not_as_described', label: 'Not as Described' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'damaged_in_transit', label: 'Damaged in Transit' },
  { value: 'size_mismatch', label: 'Size Mismatch' },
  { value: 'colour_mismatch', label: 'Colour Mismatch' },
  { value: 'other', label: 'Other' },
]

export default function ReturnsPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ order_id: '', type: 'return', reason: '', description: '' })

  const { data, isLoading } = useQuery({
    queryKey: ['buyer-returns'],
    queryFn: () => returnsApi.list().then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: (d) => returnsApi.create(d),
    onSuccess: () => {
      toast.success('Return request submitted')
      setShowForm(false)
      setForm({ order_id: '', type: 'return', reason: '', description: '' })
      qc.invalidateQueries({ queryKey: ['buyer-returns'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to submit return'),
  })

  const returns = data?.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Returns & Exchanges</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> New Return
        </button>
      </div>

      {/* New return form */}
      {showForm && (
        <div className="bg-white rounded-lg border p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">Request Return/Exchange</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
              <input
                type="number"
                value={form.order_id}
                onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))}
                placeholder="Enter order ID"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="return">Return (Refund)</option>
                <option value="exchange">Exchange (Replacement)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select reason...</option>
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Describe the issue..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => createMut.mutate(form)}
              disabled={!form.order_id || !form.reason || !form.description || createMut.isPending}
              className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 disabled:opacity-50"
            >
              Submit Request
            </button>
            <button onClick={() => setShowForm(false)} className="text-gray-500 px-4 py-2 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Returns list */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : returns.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <RotateCcw className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No return requests</p>
          <p className="text-xs mt-1">Items can be returned within 7 days of delivery</p>
        </div>
      ) : (
        <div className="space-y-3">
          {returns.map(ret => (
            <div key={ret.id} className="bg-white rounded-lg border p-4 flex items-center justify-between hover:shadow-sm transition">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-gray-50 rounded-lg">
                  {ret.type === 'exchange' ? <Package className="w-5 h-5 text-indigo-500" /> : <RotateCcw className="w-5 h-5 text-amber-500" />}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{ret.return_number}</div>
                  <div className="text-xs text-gray-500">
                    Order #{ret.order?.order_number} &middot; {ret.type === 'exchange' ? 'Exchange' : 'Return'} &middot; {REASONS.find(r => r.value === ret.reason)?.label || ret.reason}
                  </div>
                  {ret.refund_amount && (
                    <div className="text-xs text-green-600 font-medium mt-0.5">
                      Refund: Rs.{Number(ret.refund_amount).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ret.status] || 'bg-gray-100 text-gray-600'}`}>
                  {ret.status?.replace(/_/g, ' ').toUpperCase()}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
